import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { omit } from "lodash";

import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import { getOptionValue } from "common/enums";
import BillMeterReadingFormItems from "./BillMeterReadingFormItems";

import { getImageUrl } from "utils";
import { getMeterReadingValidation } from "utils/getMeterReadingValidation";

import {
  useGetOtherUtilityMeterReadingDataByMeterIdQuery,
  usePostOtherUtilityUpdatedMeterReadingsMutation,
  usePutOtherUtilityMeterReadingMutation,
} from "apis/MeterReading";

const keysToDelete = ["currentReadingDifference", "meterNumber"];

const meterReadingValidationSchema = Yup.object({
  meterReadingsData: Yup.array().of(
    Yup.object().shape({
      currentReading: getMeterReadingValidation(
        "currentReadingDifference",
        "Current reading",
        "Current reading difference"
      ),
      previousMeterReading: Yup.object({
        currentReading: getMeterReadingValidation(
          "currentReadingDifference",
          "Current reading",
          "Current reading difference",
          true
        ),
      }),
    })
  ),
});

const BillMeterReadingForm = ({
  utilityConsumerId,
  handleClose,
  billCycleId,
  subscriptionType,
  t,
}) => {
  // Get meter reading data
  const {
    data: { data: { utilityConsumerMeterReadings, utilityConsumer } = {} } = {},
    isLoading: isConsumerMeterReadingLoading,
  } = useGetOtherUtilityMeterReadingDataByMeterIdQuery(
    billCycleId,
    utilityConsumerId
  );

  //Get url on preview click
  const [
    getUpdatedBillCalculations,
    { isLoading: isUpdatedBillCalculationLoading },
  ] = usePostOtherUtilityUpdatedMeterReadingsMutation();

  // Updating meter data
  const [updateMeterReadings, { isLoading: isMeterReadingsUpdating }] =
    usePutOtherUtilityMeterReadingMutation();

  const [documentToPreview, setDocumentToPreview] = useState("");

  const { isSolidWasteSubscriptionType, isDcsSubscriptionType } = useMemo(
    () => ({
      isSolidWasteSubscriptionType:
        subscriptionType ===
        getOptionValue("otherUtilitySubscriptionOptions", "Solid Waste"),
      isDcsSubscriptionType:
        subscriptionType ===
        getOptionValue("otherUtilitySubscriptionOptions", "DCS"),
    }),
    [subscriptionType]
  );

  const initialValues = useMemo(
    () => ({
      meterReadingsData:
        utilityConsumerMeterReadings?.map((item) => ({
          id: item?.id ?? 0,
          utilityConsumerId: utilityConsumer?.id ?? 0,
          meterId: item?.meter?.id ?? 0,
          meterNumber: item?.meter?.srNo,
          tariffId: item?.tariffId ?? "",
          currentReading: item?.currentReading ?? 0,
          adjustments: item?.adjustments ?? 0,
          ...(isDcsSubscriptionType && {
            proRataConsumption: item?.proRataConsumption ?? 0,
            actualMaxDemand: item?.actualMaxDemand ?? 0,
            returnWaterTemperaturePenaltyCharges:
              item?.returnWaterTemperaturePenaltyCharges ?? 0,
            isDemandCharge: item?.isDemandCharge ?? false,
            note: item?.note ?? "",
          }),
          currentReadingDifference: item?.currentReadingDifference ?? 0,
          previousMeterReading: {
            id: item?.previousMeterReading?.id ?? 0,
            meterId: item?.previousMeterReading?.meter?.id ?? 0,
            currentReading: item?.previousMeterReading?.currentReading ?? 0,
            adjustments: item?.previousMeterReading?.adjustments ?? 0,
            ...(isDcsSubscriptionType && {
              actualMaxDemand: item?.previousMeterReading?.actualMaxDemand ?? 0,
              proRataConsumption:
                item?.previousMeterReading?.proRataConsumption ?? 0,
              returnWaterTemperaturePenaltyCharges:
                item?.previousMeterReading
                  ?.returnWaterTemperaturePenaltyCharges ?? 0,
              isDemandCharge:
                item?.previousMeterReading?.isDemandCharge ?? false,
              note: item?.previousMeterReading?.note ?? "",
            }),
          },
        })) ?? [],
    }),
    [utilityConsumerMeterReadings, isDcsSubscriptionType, utilityConsumer]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    ...(!isSolidWasteSubscriptionType && {
      validationSchema: meterReadingValidationSchema,
    }),
    onSubmit: async (values) => {
      try {
        const payload = values.meterReadingsData.map((meterReading) =>
          meterReading.previousMeterReading.id
            ? omit(meterReading, keysToDelete)
            : omit(meterReading, [...keysToDelete, "previousMeterReading"])
        );

        const { data: { response: url } = {} } =
          await getUpdatedBillCalculations(billCycleId, payload);

        setDocumentToPreview({
          url: getImageUrl(url),
          documentTypeName: "Bill",
          documentType: "pdf",
          documentName: "bill",
        });
      } catch (error) {}
    },
  });

  const { values, handleSubmit } = formik;

  const approveBtnHandler = async () => {
    try {
      const payload = values.meterReadingsData.map((meterReading) =>
        meterReading.previousMeterReading.id
          ? omit(meterReading, keysToDelete)
          : omit(meterReading, [...keysToDelete, "previousMeterReading"])
      );
      await updateMeterReadings(billCycleId, payload);
      handleClose();
    } catch (error) {}
  };

  if (isConsumerMeterReadingLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          actionButton={
            <Button
              color="success"
              onClick={approveBtnHandler}
              variant="contained"
              disabled={isMeterReadingsUpdating}
            >
              {t("Approve")}
              {isMeterReadingsUpdating && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title="Edit Meter Readings"
        maxWidth="md"
      >
        <Grid container columnGap={1} className="mt-3">
          {values.meterReadingsData.map((meterReading, index) => (
            <BillMeterReadingFormItems
              meterReading={meterReading}
              formik={formik}
              index={index}
              key={index}
              subscriptionType={subscriptionType}
            />
          ))}
          <Box className="d-block ms-auto" mt={2}>
            <Button
              variant="contained"
              size="medium"
              color="primary"
              onClick={handleSubmit}
              disabled={isUpdatedBillCalculationLoading}
            >
              {t("Preview")}
              {isUpdatedBillCalculationLoading && (
                <CircularProgress size={20} />
              )}
            </Button>
          </Box>
        </Grid>
      </Modal>
    </>
  );
};

BillMeterReadingForm.propTypes = {
  utilityConsumerId: PropTypes.number,
  handleClose: PropTypes.func,
  billCycleId: PropTypes.number,
  subscriptionType: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(BillMeterReadingForm);
